import { useContext } from 'react'

import moment from 'moment'
import useSwr from 'swr'

import { faArrowPointer } from '@driftt/dds.icons/light/faArrowPointer'
import { faCalendarCheck } from '@driftt/dds.icons/light/faCalendarCheck'
import { faCameraWeb } from '@driftt/dds.icons/light/faCameraWeb'
import { faCirclePlay } from '@driftt/dds.icons/light/faCirclePlay'
import { faComment } from '@driftt/dds.icons/light/faComment'
import { faEnvelope } from '@driftt/dds.icons/light/faEnvelope'
import { faEnvelopeOpen } from '@driftt/dds.icons/light/faEnvelopeOpen'
import { faEye } from '@driftt/dds.icons/light/faEye'
import { faInboxIn } from '@driftt/dds.icons/light/faInboxIn'
import { faInboxOut } from '@driftt/dds.icons/light/faInboxOut'
import { faReply } from '@driftt/dds.icons/light/faReply'
import { faSquareQuestion } from '@driftt/dds.icons/light/faSquareQuestion'

import { fetchSalesforceTasks } from 'api'
import { DateRange } from 'components/DateRangePicker'
import { SortContext } from 'context/sortContext'
import { ActivityType, Insight } from 'modules/accountActivity/domain'
import { FilterPage } from 'utils/filters'
import { getTaskTypeFromSubject, removeTypeFromSubjectLine } from 'utils/salesforce'

import { useInsightsFeed } from './useInsightsFeed'

const getIconByType = (type: string) => {
  switch (type) {
    case 'Email':
      return faEnvelope
    case ActivityType.PAGE_VIEW:
      return faEye
    case 'Email Opened':
      return faEnvelopeOpen
    case 'Outbound Email':
      return faInboxOut
    case 'Inbound Email':
      return faInboxIn
    case 'Video Call':
      return faCameraWeb
    case 'Email Reply':
      return faReply
    case 'Email Clicked':
      return faArrowPointer
    case 'Chat':
    case ActivityType.CONVERSATION:
      return faComment
    case ActivityType.MEETING_BOOKED:
      return faCalendarCheck
    case ActivityType.VIDEO:
      return faCirclePlay
    default:
      return faSquareQuestion
  }
}

interface SalesforceTasksArgs {
  accountId?: string
  dateRange?: DateRange
}

const useAccountActivities = ({ accountId, dateRange }: SalesforceTasksArgs) => {
  const { activitiesSort, activitiesDescending } = useContext(SortContext)
  const { startDate, endDate } = dateRange || {}
  const { data: tasksData, error: tasksError } = useSwr(
    accountId ? `${accountId}-${startDate}-${endDate}` : null,
    async () => {
      const query: Record<string, string> = {}
      if (startDate && endDate) {
        query.startDate = moment(startDate).format('YYYY-MM-DD')
        query.endDate = moment(endDate).format('YYYY-MM-DD')
      }
      return await fetchSalesforceTasks({ accountId: `${accountId}`, ...query })
    }
  )
  const { insights } = useInsightsFeed(
    FilterPage.ACTIVITIES,
    activitiesSort,
    activitiesDescending,
    10,
    !!(startDate && endDate),
    false,
    false,
    accountId,
    undefined,
    dateRange
  )

  const normalizedInsights = insights.data.map((insight: Insight) => {
    const { id, body, lastActivityAt, numActivities } = insight

    const activities = body.activityGroupsByEndUser?.reduce(
      (acc: any, { activityCounts, activityList }: Record<string, any> = {}) => {
        const activityListData = acc.activityList ?? []
        acc.counts = acc.counts ?? {}

        for (const key in activityCounts) {
          acc.counts[key] = acc.counts[key]
            ? acc.counts[key] + activityCounts[key]
            : activityCounts[key]
        }

        return {
          ...acc,
          activityList: [...activityListData, ...activityList]
        }
      },
      {}
    )

    return {
      id,
      date: lastActivityAt,
      numActivities,
      type: activities?.counts ? Object.keys(activities?.counts)[0] : 'Other',
      cardType: 'DRIFT_INSIGHT',
      ...activities
    }
  })

  const normalizedTasks = tasksData
    ? tasksData.map(({ CreatedDate, Type, Subject, ...rest }: any) => ({
        ...rest,
        date: CreatedDate,
        Subject: removeTypeFromSubjectLine(Subject),
        type: getTaskTypeFromSubject(Subject) || 'Other',
        cardType: 'SALESFORCE'
      }))
    : []

  const groupedActivities = [...normalizedInsights, ...normalizedTasks]
    ?.sort(({ date: a }: any, { date: b }: any) => (moment(a).isBefore(b) ? 1 : -1))
    .reduce((acc: any, task: any) => {
      const { date: Date, type, Id, id } = task
      const date = moment(Date).format('MM/DD/YYYY')
      const currentDateData = acc[date] ? acc[date] : {}
      const currentTaskTypes = (acc[date] && acc[date][type]) ?? []

      if (type === 'Other') {
        return acc
      }

      return {
        ...acc,
        [date]: {
          ...currentDateData,
          [type]: [
            ...currentTaskTypes,
            {
              ...task,
              id: Id || id,
              date: moment(Date).format('MM/DD/YYYY hh:mm A'),
              icon: getIconByType(type)
            }
          ]
        }
      }
    }, {})

  return {
    data: groupedActivities,
    error: tasksError
  }
}

export default useAccountActivities
